import type { PulseClientConfig } from "./config";
import { normalizeBaseUrl } from "./config";
import { parseErrorResponse } from "./errors";

export type PulseRequestInit = {
  method: "GET" | "POST";
  body?: unknown;
  query?: Record<string, string | undefined>;
};

function buildUrl(baseUrl: string, path: string, query?: Record<string, string | undefined>): string {
  const params = new URLSearchParams();
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) params.set(key, value);
    }
  }
  const queryString = params.toString();
  return `${normalizeBaseUrl(baseUrl)}${path}${queryString ? `?${queryString}` : ""}`;
}

async function performRequest(
  config: PulseClientConfig,
  path: string,
  init: PulseRequestInit,
  accessToken: string | undefined,
): Promise<Response> {
  const headers: Record<string, string> = {};
  if (init.body !== undefined) headers["Content-Type"] = "application/json";
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  return globalThis.fetch(buildUrl(config.baseUrl, path, init.query), {
    method: init.method,
    headers,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
}

async function resolveAccessToken(config: PulseClientConfig): Promise<string | undefined> {
  if (config.getAccessToken) return config.getAccessToken();
  return config.accessToken;
}

export async function pulseRequest<T>(
  config: PulseClientConfig,
  path: string,
  init: PulseRequestInit,
): Promise<T> {
  let response = await performRequest(config, path, init, await resolveAccessToken(config));

  if (response.status === 401 && config.getAccessToken) {
    response = await performRequest(config, path, init, await config.getAccessToken());
  }

  if (!response.ok) {
    throw await parseErrorResponse(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}
